/**
 * API 封装模块
 * 使用 axios 进行网络请求
 * 包含请求拦截器、响应拦截器和常用请求方法
 */
import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import qs from 'qs';
import MD5 from 'crypto-js/md5';

export type QueryParams = Record<string, string | number | boolean | null | undefined>;
export type RequestData = Record<string, unknown> | unknown[] | string | number | boolean | null | undefined;

export interface ApiResponse<T = unknown> {
  retCode?: string;
  retDesc?: string;
  data: T;
  [key: string]: unknown;
}

export interface ApiRequestConfig<D = unknown> extends AxiosRequestConfig<D> {
  headers?: Record<string, string>;
}

const generateUUID = (): string =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c: string) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

const SIGN_SECRET = import.meta.env.VITE_SIGN_SECRET || 'hello wold';

const md5 = async (text: string): Promise<string> => {
  return MD5(text).toString();
};

const normalizeBody = (data: unknown): string => {
  if (data == null || data === '') return '';
  if (typeof data === 'string') return data;
  return JSON.stringify(data);
};

const applySignHeaders = async <D = unknown>(config: AxiosRequestConfig<D>): Promise<AxiosRequestConfig<D>> => {
  const ts = Date.now();
  const body = normalizeBody(config.data);
  const sign = await md5(`${body}${SIGN_SECRET}${ts}`);

  const headers = (config.headers ?? {}) as Record<string, string>;
  headers.sign = sign;
  headers['req-time'] = String(ts);
  config.headers = headers;

  return config;
};

// 所有 axios 实例统一使用登录域 baseURL（env 可覆盖：VITE_LOGIN_BASE_URL）
// 业务接口（instance）在此基础上追加 /cmtp 前缀，认证/计费接口（login）直接使用域名
const API_BASE_URL = import.meta.env.VITE_LOGIN_BASE_URL || 'https://app-dev-api.oakcreektech.com';
const API_BASE_DOMAIN = API_BASE_URL.replace(/\/+$/, '');
const CMTP_PATH_PREFIX = '/cmtp';

const createClient = (baseURL: string): AxiosInstance => {
  return axios.create({
    baseURL,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
    paramsSerializer: {
      serialize: (params) => qs.stringify(params, { arrayFormat: 'brackets', encode: false }),
    },
  });
};

const instance = createClient(`${API_BASE_DOMAIN}${CMTP_PATH_PREFIX}`);
const login = createClient(API_BASE_URL);

const getStoredToken = (): string => {
  return localStorage.getItem('auth_token') || '';
};

export const getStoredUserId = (): string => {
  const raw = localStorage.getItem('user_id') || '';

  const normalized =
    raw &&
    raw !== 'undefined' &&
    raw !== 'null' &&
    String(raw).trim() !== ''
      ? String(raw).trim()
      : '';

  return normalized;
  // "a5c4079d5f847c2f25ebcd54457de58e";
};

instance.interceptors.request.use(
  async (config) => {
    const userId = getStoredUserId();
    const token = getStoredToken();
    const headers = (config.headers ?? {}) as Record<string, string>;

    if (userId) {
      headers['x-user-id'] = String(userId);
    }

    headers['x-request-id'] = generateUUID();

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    config.headers = headers;

    await applySignHeaders(config);

    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

login.interceptors.request.use(
  async (config) => {
    const token = getStoredToken();
    const headers = (config.headers ?? {}) as Record<string, string>;

    if (token) {
      headers.Authorization = `Bearer ${token}`;
      headers.token = token;
    }

    config.headers = headers;

    console.log('[login request]', {
      url: config.url,
      baseURL: config.baseURL,
      tokenInjected: !!token,
      tokenPrefix: token ? `${token.slice(0, 6)}...` : '',
      params: config.params,
      data: config.data,
    });

    await applySignHeaders(config);
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// token 失效：后端以 HTTP 200 + retCode 100008 返回（而非 HTTP 401），需在响应成功分支里拦截
const TOKEN_INVALID_RET_CODE = '12580';

// 跳转登录页（带冷却期，避免并发请求重复触发）
let lastUnauthorizedAt = 0;
const UNAUTHORIZED_COOLDOWN_MS = 3000;

const handleUnauthorized = (): void => {
  const now = Date.now();
  if (now - lastUnauthorizedAt < UNAUTHORIZED_COOLDOWN_MS) return;
  lastUnauthorizedAt = now;

  localStorage.removeItem('auth_token');
  localStorage.removeItem('user_id');
  sessionStorage.setItem('login_expired', '1');

  if (window.location.pathname !== '/login') {
    window.location.replace('/login');
  }
};

const isTokenInvalid = (body: unknown): boolean =>
  typeof body === 'object' && body !== null && (body as { retCode?: unknown }).retCode === TOKEN_INVALID_RET_CODE;

const handleResponseError = (error: AxiosError): Promise<never> => {
  console.error('Response Interceptor Error:', error);

  if (error.response) {
    const { status, data } = error.response;

    if (status === 401) {
      handleUnauthorized();
    } else if (status === 500) {
      console.error('Server error, please try again later!');
    } else {
      const message = typeof data === 'object' && data && 'message' in data ? String((data as { message?: unknown }).message) : 'An error occurred!';
      console.error(message);
    }
  } else if (error.request) {
    console.error('No response from server!');
  } else {
    console.error(`Request failed: ${error.message}`);
  }

  return Promise.reject(error);
};

instance.interceptors.response.use(
  (response: AxiosResponse<any>) => {
    if (response.status >= 200 && response.status < 300) {
      if (isTokenInvalid(response.data)) {
        handleUnauthorized();
        return Promise.reject(new Error('登录已过期，请重新登录'));
      }
      return response.data;
    }
    return Promise.reject(new Error(`Unexpected response status: ${response.status}`));
  },
  handleResponseError,
);

login.interceptors.response.use(
  (response: AxiosResponse<unknown>) => {
    if (isTokenInvalid(response.data)) {
      handleUnauthorized();
      return Promise.reject(new Error('登录已过期，请重新登录'));
    }
    return response;
  },
  handleResponseError,
);

const get = async <T = unknown>(url: string, params: QueryParams = {}, config: ApiRequestConfig = {}): Promise<T> => {
  try {
    return await instance.get<T, T>(url, { ...config, params });
  } catch (error) {
    console.error('GET request error:', error);
    return Promise.reject(error);
  }
};

const post = async <T = unknown>(url: string, data: RequestData = {}, config: ApiRequestConfig = {}): Promise<T> => {
  try {
    return await instance.post<T, T>(url, data, config);
  } catch (error) {
    console.error('POST request error:', error);
    return Promise.reject(error);
  }
};

const put = async <T = unknown>(url: string, data: RequestData = {}, config: ApiRequestConfig = {}): Promise<T> => {
  try {
    return await instance.put<T, T>(url, data, config);
  } catch (error) {
    console.error('PUT request error:', error);
    return Promise.reject(error);
  }
};

const patch = async <T = unknown>(url: string, data: RequestData = {}, config: ApiRequestConfig = {}): Promise<T> => {
  try {
    return await instance.patch<T, T>(url, data, config);
  } catch (error) {
    console.error('PATCH request error:', error);
    return Promise.reject(error);
  }
};

const uploadFile = async <T = unknown>(
  url: string,
  file: File | Blob,
  fileType = 'video/mp4',
  onProgress?: (progress: number) => void,
  config: ApiRequestConfig = {},
): Promise<T> => {
  const formData = new FormData();
  formData.append('file', file);

  const headers = {
    'Content-Type': 'multipart/form-data',
    'File-Type': fileType,
    ...(config.headers ?? {}),
  };

  const userId = getStoredUserId();
  console.log('[DEBUG] userId 的值为:', userId, '类型:', typeof userId);
  if (userId) {
    headers['user_id'] = String(userId);
  } else {
    console.error('[ERROR] userId 为空或未定义，导致没有添加到请求头！');
  }

  try {
    return await instance.put<T, T>(url, formData, {
      ...config,
      headers,
      onUploadProgress: (progressEvent: ProgressEvent) => {
        const total = progressEvent.total;
        if (total) {
          const progress = Math.round((progressEvent.loaded * 100) / total);
          onProgress?.(progress);
        }
      },
    });
  } catch (error) {
    console.error('Upload error:', error);
    return Promise.reject(error);
  }
};

/** 直传文件到 OSS 预签名 URL - 后端 sign_url 未签入 Content-Type，请求必须不带 Content-Type 头，否则 OSS 报 SignatureDoesNotMatch。
 *  用 fetch + 空 type 的 Blob：既没有 axios 对 PUT 的默认 Content-Type（application/x-www-form-urlencoded），
 *  浏览器也不会按原 File 的 MIME 自动补 Content-Type。 */
const uploadToUrl = async (url: string, file: File | Blob): Promise<void> => {
  const res = await fetch(url, {
    method: 'PUT',
    body: new Blob([file], { type: '' }),
  });
  if (!res.ok) {
    throw new Error(`OSS 直传失败: ${res.status}`);
  }
};

const del = async <T = unknown>(url: string, config: ApiRequestConfig = {}): Promise<T> => {
  try {
    return await instance.delete<T, T>(url, config);
  } catch (error) {
    console.error('DELETE request error:', error);
    return Promise.reject(error);
  }
};

/** 媒体类型：video 视频 / text 文本 / music 音乐 */
export type MediaType = 'video' | 'text' | 'music';

export interface MediaUploadUrlResponse {
  media_id: string;
  upload_url: string;
  media_name?: string;
  media_type?: string;
  created_timestamp?: string;
  updated_timestamp?: string;
  upload_cover_url?: string;
  upload_audio_url?: string;
}

export interface MediaUpdatePayload {
  media_name?: string;
  duration?: number;
  status?: string;
}

/** 更新媒体信息响应 data 字段（对应后端 MediaResponse） */
export interface MediaUpdateResponse {
  media_id: string;
  media_name: string;
  media_type: MediaType;
  created_timestamp: string;
  updated_timestamp: string;
  [key: string]: unknown;
}

export interface TaskCreatePayload {
  media_id: string;
  task_name: string;
  robot_model: string;
  payment_id: string;
}

export interface TaskCreateResponse {
  task_id?: string;
  media_id?: string;
  simulation_id?: string;
  model_id?: string;
  task_name?: string;
  robot_model?: string;
  status?: string;
  stage?: string;
  product_type?: string;
  created_timestamp?: string;
  updated_timestamp?: string;
  [key: string]: unknown;
}

/** 任务列表查询参数 */
export interface SimulationCreatePayload {
  /** 平台标识，专为编辑平台固定传入 "edit" */
  platform: string;
  /** 已上传媒体的 ID */
  media_id: string;
  /** 仿真名称 */
  simulation_name: string;
  /** 机器人型号（下划线格式，如 booster_k1） */
  robot_model: string;
}

export interface SimulationCreateResponse {
  simulation_id: string;
  media_id: string;
  simulation_name: string;
  robot_model: string;
  status: string;
  progress: number;
  created_timestamp: string;
  updated_timestamp: string;
  task_id: string;
}

export interface ModelCreatePayload {
  /** 所属仿真的 ID */
  simulation_id: string;
  /** 模型名称 */
  model_name?: string;
}

export interface ModelCreateResponse {
  model_id: string;
  simulation_id: string;
  model_name: string;
  robot_model: string;
  status: string;
  progress: number;
  export: boolean;
  created_timestamp: string;
  updated_timestamp: string;
  task_id: string;
}

/** 训练商品条目 */
export interface ProductSchema {
  product_id: string;
  product_name: string;
  description: string;
  topic: string | null;
  is_recommended: boolean;
}

/** 训练商品列表查询参数 */
export interface ProductListQuery {
  page_size: number;
  page_index: number;
  training_entrance: 'simulation' | 'model';
}

export interface ProductListResponse {
  products: ProductSchema[];
}

/** 商品报价查询参数 */
export interface ProductQuoteQuery {
  media_id: string;
  product_id: string;
  /** 专为编辑平台，可传入 "edit"，其他平台不需要 */
  platform?: string;
}

export interface ProductQuoteResponse {
  quote: number;
}

export interface TaskListQuery {
  /** 每页条数 */
  limit: number;
  /** 偏移量 */
  offset: number;
  /** 任务名模糊搜索 */
  task_name?: string;
  /** 机器人型号 */
  robot_model?: string;
  /** 来源：user 用户 / system 系统 */
  origin_type?: 'user' | 'system';
}

/** 任务列表项（后端 TaskList 模型） */
export interface TaskListItem {
  task_id: string;
  media_id?: string | null;
  simulation_id?: string | null;
  model_id?: string | null;
  task_name: string;
  robot_model: string;
  status: string;
  stage: string;
  product_type: number;
  created_timestamp: string;
  updated_timestamp: string;
  progress: number;
  duration: number;
  cover_url?: string | null;
  origin_type: string;
  media_type: string;
}

/** 任务列表响应 data 字段 */
export interface TaskListResponse {
  items: TaskListItem[];
  has_more: boolean;
  limit: number;
  offset: number;
}

/** 任务详情响应 data 字段（单条任务，含失败详情链接） */
export interface TaskDetailResponse {
  task_id?: string;
  media_id?: string | null;
  simulation_id?: string | null;
  model_id?: string | null;
  task_name?: string;
  robot_model?: string;
  status?: string;
  stage?: string;
  product_type?: number;
  created_timestamp?: string;
  updated_timestamp?: string;
  progress?: number;
  duration?: number;
  cover_url?: string | null;
  origin_type?: string;
  media_type?: string;
  failure_detail_url?: string | null;
}

/** 双脚调平请求参数 */
export interface FootLevelingRequest extends Record<string, unknown> {
  /** 轨迹数据数组，每项为 URDF关节名 → 弧度值的映射 */
  trajectory_data: Record<string, unknown>[];
  /** 起始帧 */
  start_frame: number;
  /** 结束帧 */
  end_frame: number;
  /** 机器人型号，如 "booster_k1" */
  robot_model: string;
}

/** 双脚调平响应 */
export interface FootLevelingResponse {
  /** 调整后的轨迹数据，格式与输入 trajectory_data 一致 */
  data: Record<string, unknown>[];
}

/** 智能调脚请求参数 */
export interface SmartFootAdjustRequest {
  /** 轨迹数据数组，每项为 URDF关节名 → 弧度值的映射 */
  trajectory_data: Record<string, unknown>[];
  /** 起始帧 */
  start_frame: number;
  /** 结束帧 */
  end_frame: number;
  /** 机器人型号，如 "booster_k1" */
  robot_model: string;
  /** 调整范围：auto | left | right | both */
  scope: string;
}

/** 智能调脚响应 */
export interface SmartFootAdjustResponse {
  /** 调整后的轨迹数据，格式与输入 trajectory_data 一致 */
  data: Record<string, unknown>[];
}

/** 锁踝调腰请求参数（roll / pitch 共用） */
export interface WaistAdjustRequest extends Record<string, unknown> {
  /** 轨迹数据数组，每项为 URDF关节名 → 弧度值的映射 */
  trajectory_data: Record<string, unknown>[];
  /** 起始帧 */
  start_frame: number;
  /** 结束帧 */
  end_frame: number;
  /** 机器人型号，如 "booster_k1" */
  robot_model: string;
  /** 调整轴：roll 或 pitch */
  axis: 'roll' | 'pitch';
}

/** 锁踝调腰响应 */
export interface WaistAdjustResponse {
  /** 调整后的轨迹数据，格式与输入 trajectory_data 一致 */
  data: Record<string, unknown>[];
}

/** ZMP风险检测请求参数 */
export interface ZmpRiskCheckRequest extends Record<string, unknown> {
  /** 轨迹数据数组，每项为 URDF关节名 → 弧度值的映射 */
  trajectory_data: Record<string, unknown>[];
  /** 起始帧 */
  start_frame: number;
  /** 结束帧 */
  end_frame: number;
  /** 机器人型号，如 "booster_k1" */
  robot_model: string;
}

/** ZMP风险检测 - 各方向风险详情 */
export interface ZmpDirectionRisk {
  [direction: string]: unknown;
}

/** ZMP风险检测 - 单帧风险详情 */
export interface ZmpRiskDetail {
  [key: string]: unknown;
}

/** 获取仿真已编辑帧序号列表响应 - edit_set 为被修改过的帧索引数组 */
export interface EditSetResponse {
  edit_set: number[];
}

/** 获取仿真播放总时长响应 */
export interface SimulationDurationResponse {
  duration: number;
}

/** 下载仿真 CSV（JSON 行数组）响应 data 字段 */
export interface SimulationCsvResponse {
  /** CSV 行数组，每行为 URDF 关节名 → 弧度值的映射 */
  data: Record<string, unknown>[];
}

/** 橡树果账户余额响应 data 字段 */
export interface BillingAccountInfo {
  /** 可用余额 */
  availableAmount: number;
}

/** 橡树果使用明细查询参数 */
export interface BillingLedgerQuery {
  /** 页码 */
  pageNo?: number;
  /** 每页条数 */
  pageSize?: number;
  /** 方向：1 全部 / 2 获得 / 3 消耗 */
  direction?: number;
}

/** 橡树果使用明细条目 */
export interface BillingLedgerItem {
  /** 标题，如 "充值获得" */
  title: string;
  /** 创建时间戳（毫秒） */
  createdAt: number;
  /** 金额 */
  amount: number;
  /** 方向：income 获得 / expense 消耗 */
  direction: string;
}

/** 充值商品列表查询参数 */
export interface BillingRechargeSkuQuery {
  /** 平台：1 Apple / 2 其他平台 */
  platform?: number;
}

/** 充值商品条目 */
export interface BillingRechargeSku {
  /** 商品 SKU ID */
  skuId: string;
  /** 商品 ID */
  productId: string;
  /** 现金金额（元） */
  cashAmount: number;
  /** 基础虚拟币数量 */
  virtualBaseAmount: number;
  /** 赠送虚拟币数量 */
  virtualGiftAmount: number;
}

/** 创建充值订单请求参数 */
export interface BillingRechargeOrderRequest {
  /** 支付方式：1 支付宝 / 2 微信 / 3 Apple */
  paymentProvider: number;
  /** 充值类型：1 固定 SKU / 2 自定义金额 */
  rechargeType: number;
  /** 充值金额（1～9999 的整数） */
  rechargeAmount: number;
  /** 平台：1 APP / 2 WAP / 3 WEB */
  platform: number;
  /** SKU ID，固定档位时传入 */
  skuId?: string;
  /** 商品 ID，Apple 支付时传入 */
  productId?: string;
}

/** 创建充值订单响应 data 字段 */
export interface BillingRechargeOrderResponse {
  /** 支付订单 ID */
  paymentOrderId: string;
  /** 支付渠道名（如 alipay） */
  paymentAction: string;
  /** 支付参数 */
  paymentParams: {
    /** 支付宝跳转表单 HTML（注入页面后自动提交） */
    pageRedirectionData?: string;
    /** 微信 NATIVE 二维码内容（code_url，前端渲染为二维码） */
    codeUrl?: string;
  };
}

/** 查询支付结果响应 data 字段 */
export interface BillingPaymentResult {
  /** 支付订单状态：1 创建中 / 2 待支付 / 3 已支付 / 4 已充值到账 / 5 失败 / 6 关闭 / 7 已退款 / 8 已删除 / 9 已取消 / 10 已超时 */
  paymentOrderStatus: number;
  /** 充值金额 */
  rechargeAmount: number;
  /** 充值成功提示信息 */
  rechargeSuccessMessage: string;
}

/** ZMP风险检测响应 */
export interface ZmpRiskCheckResponse {
  /** 综合风险评分 0-100 */
  score: number;
  /** 高风险帧号列表 */
  high_risk: number[];
  /** 警告风险帧号列表 */
  warning_risk: number[];
  /** 各方向风险分布 */
  by_direction: ZmpDirectionRisk;
  /** 逐帧风险详情 */
  details: ZmpRiskDetail[];
  /** 最高风险时刻描述 */
  worst_text: string;
  /** 风险摘要 */
  summary: string;
  /** 完整风险报告 */
  report: string;
}

/** 验证码登录请求参数 */
export interface LoginByCodeRequest {
  /** 手机号 */
  mobile: string;
  /** 短信验证码 */
  verCode: string;
  /** 未注册自动注册时使用的邀请/推广码 */
  spreadCode?: string;
}

/** 登录用户信息 */
export interface LoginUserInfo {
  avatarUrl: string;
  createDate: string;
  mobile: string;
  userId: string;
  userName: string;
  /** vip 图标 url 地址 */
  vipIcon: string;
}

/** 验证码登录响应 data 字段 */
export interface LoginByCodeResponse {
  token: string;
  userInfo: LoginUserInfo;
  /** 是否新用户 */
  isNewUser: boolean;
}

/** 发送验证码请求参数 */
export interface SendSmsRequest {
  /** 手机号 */
  mobile: string;
  /** 场景：login 登录 / bindMob 换绑手机号 */
  type: 'login' | 'bindMob';
}

/** 发送验证码响应 data 字段 */
export interface SendSmsResponse {
  /** 验证码过期时间（秒） */
  time: number;
  /** 客户端提示文案 */
  message: string;
  /** 短信是否发送成功 */
  status: boolean;
}

const api = {
  get,
  post,
  put,
  patch,
  delete: del,
  uploadFile,
  uploadToUrl,
  media: {
    /** 获取媒体上传地址 - 返回 media_id 与直传 URL，响应为 retCode/retDesc/data 信封 */
    createUploadUrl: async (mediaName: string): Promise<MediaUploadUrlResponse> => {
      const res = await instance.post<ApiResponse<MediaUploadUrlResponse>, ApiResponse<MediaUploadUrlResponse>>(
        '/api/v1/media/upload-url',
        { media_name: mediaName },
      );
      if (res.retCode !== '000000') return Promise.reject(new Error(res.retDesc || '获取上传地址失败'));
      return res.data;
    },
    /** 更新媒体信息 - 回填时长与状态，响应为 retCode/retDesc/data 信封 */
    updateMedia: async (mediaId: string, data: MediaUpdatePayload): Promise<MediaUpdateResponse> => {
      const res = await instance.patch<ApiResponse<MediaUpdateResponse>, ApiResponse<MediaUpdateResponse>>(
        `/api/v1/media/${mediaId}`,
        data,
      );
      if (res.retCode !== '000000') return Promise.reject(new Error(res.retDesc || '更新媒体信息失败'));
      return res.data;
    },
  },
  task: {
    /** 创建任务 - POST /api/v1/task，响应为 retCode/retDesc/data 信封 */
    create: async (data: TaskCreatePayload): Promise<TaskCreateResponse> => {
      const res = await instance.post<ApiResponse<TaskCreateResponse>, ApiResponse<TaskCreateResponse>>(
        '/api/v1/task',
        data,
      );
      if (res.retCode !== '000000') return Promise.reject(new Error(res.retDesc || '创建任务失败'));
      return res.data;
    },
    /** 查询任务列表 - GET /api/v1/task，响应为 retCode/retDesc/data 信封，需自行解析后返回 data */
    list: async (params: TaskListQuery): Promise<TaskListResponse> => {
      const res = await instance.get<ApiResponse<TaskListResponse>, ApiResponse<TaskListResponse>>(
        '/api/v1/task',
        { params },
      );
      if (res.retCode !== '000000') return Promise.reject(new Error(res.retDesc || '获取任务列表失败'));
      return res.data;
    },
    /** 查询任务详情 - GET /api/v1/task/{id}，响应为 retCode/retDesc/data 信封 */
    detail: async (
      taskId: string,
      params: { origin_type?: 'user' | 'system' } = {},
      config: ApiRequestConfig = {},
    ): Promise<TaskDetailResponse> => {
      const res = await instance.get<ApiResponse<TaskDetailResponse>, ApiResponse<TaskDetailResponse>>(
        `/api/v1/task/${taskId}`,
        { ...config, params },
      );
      if (res.retCode !== '000000') return Promise.reject(new Error(res.retDesc || '获取任务详情失败'));
      return res.data;
    },
  },
  simulation: {
    /** 创建仿真 - POST /api/v1/simulation，响应为 retCode/retDesc/data 信封 */
    create: async (data: SimulationCreatePayload): Promise<SimulationCreateResponse> => {
      const res = await instance.post<ApiResponse<SimulationCreateResponse>, ApiResponse<SimulationCreateResponse>>(
        '/api/v1/simulation',
        data,
      );
      if (res.retCode !== '000000') return Promise.reject(new Error(res.retDesc || '创建仿真失败'));
      return res.data;
    },
    /** 智能调脚 - 对指定帧范围进行足部姿态调整，响应为 retCode/retDesc/data 信封 */
    smartFootAdjust: async (data: SmartFootAdjustRequest): Promise<SmartFootAdjustResponse> => {
      const res = await instance.post<ApiResponse<SmartFootAdjustResponse>, ApiResponse<SmartFootAdjustResponse>>(
        '/api/v1/edit/smart-foot-adjust',
        data,
      );
      if (res.retCode !== '000000') return Promise.reject(new Error(res.retDesc || '智能调脚失败'));
      return res.data;
    },
    /** 双脚调平 - 对指定帧范围进行双脚水平调整，响应为 retCode/retDesc/data 信封 */
    footLeveling: async (data: FootLevelingRequest): Promise<FootLevelingResponse> => {
      const res = await instance.post<ApiResponse<FootLevelingResponse>, ApiResponse<FootLevelingResponse>>(
        '/api/v1/edit/foot-leveling',
        data,
      );
      if (res.retCode !== '000000') return Promise.reject(new Error(res.retDesc || '双脚调平失败'));
      return res.data;
    },
    /** ZMP风险检测 - 评估指定帧范围的稳定性风险，响应为 retCode/retDesc/data 信封 */
    zmpRiskCheck: async (data: ZmpRiskCheckRequest): Promise<ZmpRiskCheckResponse> => {
      const res = await instance.post<ApiResponse<ZmpRiskCheckResponse>, ApiResponse<ZmpRiskCheckResponse>>(
        '/api/v1/edit/zmp-risk-check',
        data,
      );
      if (res.retCode !== '000000') return Promise.reject(new Error(res.retDesc || 'ZMP风险检测失败'));
      return res.data;
    },
    /** 锁踝调腰 - roll/pitch 方向腰部姿态调整，响应为 retCode/retDesc/data 信封 */
    waistAdjust: async (data: WaistAdjustRequest): Promise<WaistAdjustResponse> => {
      const res = await instance.post<ApiResponse<WaistAdjustResponse>, ApiResponse<WaistAdjustResponse>>(
        '/api/v1/edit/ankle-locked-base-upright',
        data,
      );
      if (res.retCode !== '000000') return Promise.reject(new Error(res.retDesc || '锁踝调腰失败'));
      return res.data;
    },
    /** 获取仿真播放总时长（秒），响应为 retCode/retDesc/data 信封 */
    getDuration: async (simulationId: string): Promise<SimulationDurationResponse> => {
      const res = await instance.get<ApiResponse<SimulationDurationResponse>, ApiResponse<SimulationDurationResponse>>(
        `/api/v1/simulation/${simulationId}/duration`,
      );
      if (res.retCode !== '000000') return Promise.reject(new Error(res.retDesc || '获取时长失败'));
      return res.data;
    },
    /** 获取仿真编辑区间列表 - 返回该仿真所有历史编辑过的帧区间，响应为 retCode/retDesc/data 信封 */
    getEditSet: async (simulationId: string): Promise<EditSetResponse> => {
      const res = await instance.get<ApiResponse<EditSetResponse>, ApiResponse<EditSetResponse>>(
        `/api/v1/simulation/${simulationId}/edit-set`,
      );
      if (res.retCode !== '000000') return Promise.reject(new Error(res.retDesc || '获取编辑区间失败'));
      return res.data;
    },
    /** 下载仿真CSV数据 - source: "current"(当前最新) | "backup"(初始备份)，响应为 retCode/retDesc/data 信封，需自行解析 */
    downloadCsv: async (
      simulationId: string,
      source: 'current' | 'backup' = 'current',
      config: ApiRequestConfig = {},
    ): Promise<Record<string, unknown>[]> => {
      const res = await instance.get<ApiResponse<SimulationCsvResponse>, ApiResponse<SimulationCsvResponse>>(
        `/api/v1/simulation/${simulationId}/download/csv`,
        { ...config, params: { source } },
      );
      if (res.retCode !== '000000') return Promise.reject(new Error(res.retDesc || '下载仿真数据失败'));
      return res.data?.data ?? [];
    },
  },
  model: {
    /** 创建训练模型任务 - POST /api/v1/model，响应为 retCode/retDesc/data 信封 */
    create: async (data: ModelCreatePayload): Promise<ModelCreateResponse> => {
      const res = await instance.post<ApiResponse<ModelCreateResponse>, ApiResponse<ModelCreateResponse>>(
        '/api/v1/model',
        data,
      );
      if (res.retCode !== '000000') return Promise.reject(new Error(res.retDesc || '创建训练模型失败'));
      return res.data;
    },
  },
  product: {
    /** 查询训练商品列表 - GET /api/v1/product-list，响应为 retCode/retDesc/data 信封 */
    list: async (params: ProductListQuery): Promise<ProductListResponse> => {
      const res = await instance.get<ApiResponse<ProductListResponse>, ApiResponse<ProductListResponse>>(
        '/api/v1/product-list',
        { params },
      );
      if (res.retCode !== '000000') return Promise.reject(new Error(res.retDesc || '获取商品列表失败'));
      return res.data;
    },
    /** 查询商品报价 - GET /api/v1/product-quote，响应为 retCode/retDesc/data 信封 */
    quote: async (params: ProductQuoteQuery): Promise<ProductQuoteResponse> => {
      const res = await instance.get<ApiResponse<ProductQuoteResponse>, ApiResponse<ProductQuoteResponse>>(
        '/api/v1/product-quote',
        { params },
      );
      if (res.retCode !== '000000') return Promise.reject(new Error(res.retDesc || '获取报价失败'));
      return res.data;
    },
  },
  login: {
    get: async <T = unknown>(url: string, params: QueryParams = {}, config: ApiRequestConfig = {}): Promise<T> => {
      const res = await login.get<ApiResponse<T>, AxiosResponse<ApiResponse<T>>>(url, { ...config, params });
      if (res.data.retCode !== '000000') return Promise.reject(res.data.retDesc);
      return res.data.data;
    },
    post: async <T = unknown>(url: string, data: RequestData = {}, config: ApiRequestConfig = {}): Promise<T> => {
      const res = await login.post<ApiResponse<T>, AxiosResponse<ApiResponse<T>>>(url, data, config);
      if (res.data.retCode !== '000000') return Promise.reject(res.data.retDesc);
      return res.data.data;
    },
    /** 发送验证码（新）- 手机号 + 场景类型，返回过期时间与提示文案 */
    sendSms: async (data: SendSmsRequest): Promise<SendSmsResponse> => {
      const res = await login.post<ApiResponse<SendSmsResponse>, AxiosResponse<ApiResponse<SendSmsResponse>>>(
        '/auth/api/v1/sms/send',
        data,
      );
      if (res.data.retCode !== '000000') return Promise.reject(res.data.retDesc);
      return res.data.data;
    },
    /** 验证码登录（新）- 手机号 + 短信验证码，返回 token、用户信息与是否新用户 */
    loginByCode: async (data: LoginByCodeRequest): Promise<LoginByCodeResponse> => {
      const res = await login.post<ApiResponse<LoginByCodeResponse>, AxiosResponse<ApiResponse<LoginByCodeResponse>>>(
        '/auth/api/v1/login',
        data,
      );
      if (res.data.retCode !== '000000') return Promise.reject(new Error(res.data.retDesc || '登录失败'));
      return res.data.data;
    },
  },
  billing: {
    /** 查询橡树果余额 - 返回可用余额 */
    getAccount: async (): Promise<BillingAccountInfo> => {
      const res = await login.get<ApiResponse<BillingAccountInfo>, AxiosResponse<ApiResponse<BillingAccountInfo>>>(
        '/billing/api/v1/account',
      );
      if (res.data.retCode !== '000000') return Promise.reject(res.data.retDesc);
      return res.data.data;
    },
    /** 查询橡树果使用明细 - 支持分页与方向筛选，返回明细列表 */
    getLedger: async (params: BillingLedgerQuery = {}): Promise<BillingLedgerItem[]> => {
      const res = await login.get<ApiResponse<BillingLedgerItem[]>, AxiosResponse<ApiResponse<BillingLedgerItem[]>>>(
        '/billing/api/v1/account/ledger',
        { params },
      );
      if (res.data.retCode !== '000000') return Promise.reject(res.data.retDesc);
      return res.data.data;
    },
    /** 查询充值商品列表 - 按平台返回 SKU 列表 */
    getRechargeSku: async (params: BillingRechargeSkuQuery = {}): Promise<BillingRechargeSku[]> => {
      const res = await login.get<ApiResponse<BillingRechargeSku[]>, AxiosResponse<ApiResponse<BillingRechargeSku[]>>>(
        '/billing/api/v1/account/recharge-sku',
        { params },
      );
      if (res.data.retCode !== '000000') return Promise.reject(res.data.retDesc);
      return res.data.data;
    },
    /** 立即支付创建充值订单 - 返回支付订单 ID 与支付动作 */
    createRechargeOrder: async (data: BillingRechargeOrderRequest): Promise<BillingRechargeOrderResponse> => {
      const res = await login.post<
        ApiResponse<BillingRechargeOrderResponse>,
        AxiosResponse<ApiResponse<BillingRechargeOrderResponse>>
      >('/billing/api/v1/account/recharge-order', data);
      if (res.data.retCode !== '000000') return Promise.reject(res.data.retDesc);
      return res.data.data;
    },
    /** 查询支付结果 - 按订单 ID 查询充值订单状态 */
    getPaymentResult: async (paymentOrderId: string): Promise<BillingPaymentResult> => {
      const res = await login.get<ApiResponse<BillingPaymentResult>, AxiosResponse<ApiResponse<BillingPaymentResult>>>(
        `/billing/api/v1/account/${paymentOrderId}`,
      );
      if (res.data.retCode !== '000000') return Promise.reject(res.data.retDesc);
      return res.data.data;
    },
  },
  getStoredUserId,
};

export default api;
